import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { getPost, updatePost } from "@/lib/admin-content.functions";
import { postInputSchema } from "@/lib/admin-content.schemas";
import { slugify } from "@/lib/slugify";
import { PostForm } from "@/components/admin/PostForm";

export const Route = createFileRoute("/_authenticated/_admin/admin/blog/$id/edit")({
  component: EditBlogPage,
  errorComponent: ({ error }) => <div style={{ padding: "2rem", color: "#ef4444" }}>Fehler: {(error as Error).message}</div>,
  notFoundComponent: () => <div style={{ padding: "2rem", color: "var(--text-muted)" }}>Nicht gefunden.</div>,
});

function EditBlogPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fetchPost = useServerFn(getPost);
  const submit = useServerFn(updatePost);
  const [submitting, setSubmitting] = useState(false);

  const { data: post } = useSuspenseQuery({
    queryKey: ["post", id],
    queryFn: () => fetchPost({ data: { id } }),
  });

  return (
    <PostForm
      title="Blogpost bearbeiten"
      mode="edit"
      submitting={submitting}
      defaultValues={{
        title: post.title ?? "",
        slug: post.slug ?? "",
        excerpt: post.excerpt ?? "",
        content: post.content ?? "",
        published: !!post.published,
      }}
      onSubmit={async (values) => {
        const parsed = postInputSchema.safeParse({ ...values, type: "blog", slug: values.slug || slugify(values.title) });
        if (!parsed.success) {
          toast.error("Eingabe ungültig", { description: parsed.error.issues.map(i => i.message).join("; ") });
          return;
        }
        setSubmitting(true);
        try {
          await submit({ data: { id, data: parsed.data } });
          toast.success("Blogpost aktualisiert");
          qc.invalidateQueries({ queryKey: ["admin-content"] });
          qc.invalidateQueries({ queryKey: ["post", id] });
          navigate({ to: "/admin" });
        } catch (e: any) {
          toast.error("Speichern fehlgeschlagen", { description: e?.message ?? String(e) });
        } finally {
          setSubmitting(false);
        }
      }}
    />
  );
}
